import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Flame, Package, ShieldCheck } from 'lucide-react';
import { GlassCard } from '@/components/shared/GlassCard';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { Button } from '@/components/ui/button';
import { KIT_CATEGORIES } from '@/features/trailkit/data/kit-categories';
import { useGear } from '@/features/marketplace/hooks/useGear';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { formatRupees } from '@/lib/utils/format';
import { fadeUp, inViewOnce, staggerContainer } from '@/lib/motion/variants';

export function TrailKitPreview() {
  const { data: gearItems = [] } = useGear();
  const featuredItem = gearItems[0];

  return (
    <section className="mx-auto mt-24 max-w-7xl px-4 md:px-6">
      <SectionHeader
        kicker="TrailKit · the rental shed"
        kickerColor={brand.saffron}
        title={
          <>
            Travel light,{' '}
            <em
              style={{
                display: 'inline-block',
                lineHeight: 1.1,
                paddingBottom: '0.1em',
                backgroundImage: `linear-gradient(135deg, ${brand.saffron}, ${brand.chinar})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
                fontStyle: 'italic',
              }}
            >
              rent the rest.
            </em>
          </>
        }
        description="Skis, jackets, tents, drones, climbing kit, motorbikes. We hold the inventory — you bring yourself."
        actions={
          <Button asChild variant="outline" size="sm" className="hidden md:inline-flex">
            <Link to="/trailkit">
              Browse the shed <ArrowRight size={12} />
            </Link>
          </Button>
        }
      />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={inViewOnce}
        variants={staggerContainer}
        className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6"
      >
        {KIT_CATEGORIES.map((c) => {
          const Icon = c.icon;
          return (
            <motion.div key={c.key} variants={fadeUp}>
              <Link to={c.to} className="block">
                <GlassCard
                  interactive
                  className="relative h-full overflow-hidden p-0"
                  style={{ borderColor: `${c.color}55` }}
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={unsplash(c.image, { w: 600 })}
                      alt={c.name}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-transparent" />
                    <div className="absolute left-2.5 top-2.5">
                      <span
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-white"
                        style={{ background: `${c.color}E0`, boxShadow: `0 4px 14px ${c.color}55` }}
                      >
                        <Icon size={13} />
                      </span>
                    </div>
                    <div className="absolute inset-x-2.5 bottom-2.5 text-white">
                      <div className="font-display text-[15px] font-semibold leading-tight md:text-[16px]">
                        {c.name}
                      </div>
                      <div className="mt-0.5 text-[10px] text-white/80 line-clamp-2">
                        {c.tagline}
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </Link>
            </motion.div>
          );
        })}
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={inViewOnce}
        variants={staggerContainer}
        className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_auto]"
      >
        <motion.div variants={fadeUp}>
          <GlassCard className="flex h-full items-center gap-3 p-4">
            <span
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
              style={{ background: `${brand.teal}1c`, color: brand.teal, border: `1px solid ${brand.teal}40` }}
            >
              <ShieldCheck size={16} />
            </span>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-ink">Insured · ₹25L cover</div>
              <div className="text-[11px] text-ink-mute">UO Care damage waiver on every item.</div>
            </div>
          </GlassCard>
        </motion.div>

        <motion.div variants={fadeUp}>
          <Link to="/trailkit" className="block h-full">
            <GlassCard interactive className="flex h-full items-center gap-3 p-4">
              <img
                src={featuredItem?.media_url ?? unsplash(photo.cliff, { w: 120 })}
                alt=""
                aria-hidden
                loading="lazy"
                decoding="async"
                className="h-10 w-10 shrink-0 rounded-xl object-cover"
              />
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: brand.chinar }}>
                  <Flame size={10} fill={brand.chinar} /> Hot this week
                </div>
                <div className="truncate text-[13px] font-semibold text-ink">{featuredItem?.name ?? 'Featured item'}</div>
                <div className="text-[11px] text-ink-mute">
                  {featuredItem ? formatRupees(Math.round((featuredItem.price_per_day_cents ?? 0) / 100)) : '—'}<span className="ml-0.5">/day</span>
                </div>
              </div>
              <ArrowRight size={14} className="shrink-0 text-ink-mute" />
            </GlassCard>
          </Link>
        </motion.div>

        <motion.div variants={fadeUp} className="lg:self-stretch">
          <Button asChild className="h-full w-full px-6">
            <Link to="/trailkit">
              <Package size={14} /> Browse the shed <ArrowRight size={13} />
            </Link>
          </Button>
        </motion.div>
      </motion.div>
    </section>
  );
}
